export type ReservationOccupancyIntervalErrorCode =
  | 'invalid_reservation_start'
  | 'invalid_guest_end'
  | 'inconsistent_guest_end'
  | 'invalid_duration_minutes'
  | 'invalid_preparation_minutes'
  | 'invalid_turnaround_minutes'
  | 'invalid_interval_order'
  | 'invalid_date_arithmetic'
  | 'missing_duration_source';

export interface ReservationOccupancyIntervalIssue {
  code: ReservationOccupancyIntervalErrorCode;
  message: string;
  path?: string;
}

export class ReservationOccupancyIntervalError extends Error {
  readonly issues: ReservationOccupancyIntervalIssue[];

  constructor(message: string, issues: ReservationOccupancyIntervalIssue[]) {
    super(message);
    this.name = 'ReservationOccupancyIntervalError';
    this.issues = issues;
  }
}

export interface ReservationOccupancyInterval {
  /** Guest interval start. Semantics: [reservationStart, guestEnd). */
  reservationStart: Date;
  /** Guest interval exclusive end. Semantics: [reservationStart, guestEnd). */
  guestEnd: Date;
  /** Blocked occupancy interval inclusive start under half-open semantics. */
  blockedFrom: Date;
  /** Blocked occupancy interval exclusive end under half-open semantics. */
  blockedUntil: Date;
  /** Effective guest stay duration in minutes; persisted second/millisecond precision is preserved. */
  durationMinutes: number;
  /** Effective pre-guest preparation duration in whole minutes. Current capacity policy passes 0. */
  preparationMinutes: number;
  /** Effective post-guest turnaround/buffer duration in whole minutes. */
  turnaroundMinutes: number;
}

export type ReservationOccupancyIntervalBuildResult =
  | { ok: true; interval: ReservationOccupancyInterval; issues: [] }
  | { ok: false; interval: null; issues: ReservationOccupancyIntervalIssue[] };

export interface BuildReservationOccupancyIntervalInput {
  reservationStart: Date;
  durationMinutes?: number;
  guestEnd?: Date;
  preparationMinutes?: number;
  turnaroundMinutes: number;
}

export interface ReservationTimingPolicyInput {
  /** Existing persisted guest interval start. */
  startTime?: unknown;
  /** Existing persisted guest interval end. Used to derive duration when no explicit duration override exists. */
  endTime?: unknown;
  /** Reservation-specific effective guest duration override, when one already exists. */
  durationMinutes?: unknown;
  /** Unit/default guest duration fallback. */
  defaultDurationMinutes?: unknown;
  /** Reservation-specific pre-guest preparation override, when one already exists. */
  preparationMinutes?: unknown;
  /** Reservation-specific turnaround/buffer override, when one already exists. */
  turnaroundMinutes?: unknown;
  /** Compatibility alias for turnaroundMinutes used by existing settings. */
  bufferMinutes?: unknown;
  /** Unit/default pre-guest preparation fallback. */
  defaultPreparationMinutes?: unknown;
  /** Unit/default turnaround/buffer fallback. */
  defaultTurnaroundMinutes?: unknown;
  /** Compatibility alias for defaultTurnaroundMinutes used by existing settings. */
  defaultBufferMinutes?: unknown;
}

const MINUTE_MS = 60_000;

const NON_BLOCKING_RESERVATION_OCCUPANCY_STATUSES = new Set([
  'cancelled',
  'canceled',
  'rejected',
  'declined',
  'deleted',
  'no_show',
  'no-show',
]);

export const normalizeReservationOccupancyStatus = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, '_') : '';

/** Unknown and missing statuses fail closed and continue blocking occupancy. */
export const isBlockingReservationOccupancyStatus = (value: unknown): boolean =>
  !NON_BLOCKING_RESERVATION_OCCUPANCY_STATUSES.has(normalizeReservationOccupancyStatus(value));

const issue = (
  code: ReservationOccupancyIntervalErrorCode,
  message: string,
  path?: string
): ReservationOccupancyIntervalIssue => ({ code, message, ...(path ? { path } : {}) });

export const isValidReservationDate = (value: unknown): value is Date =>
  value instanceof Date && Number.isFinite(value.getTime());

export const cloneReservationDate = (value: Date): Date => new Date(value.getTime());

const timestampLikeToDate = (value: unknown): Date | null => {
  if (isValidReservationDate(value)) return cloneReservationDate(value);
  if (value && typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    const candidate = (value as { toDate: () => unknown }).toDate();
    return isValidReservationDate(candidate) ? cloneReservationDate(candidate) : null;
  }
  return null;
};

const finiteNumber = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
};

const finiteIntegerMinutes = (value: unknown): number | null => {
  const numeric = finiteNumber(value);
  if (numeric === null) return null;
  return Math.round(numeric);
};

const addMinutes = (date: Date, minutes: number): Date | null => {
  const nextMs = date.getTime() + minutes * MINUTE_MS;
  if (!Number.isFinite(nextMs)) return null;
  const next = new Date(nextMs);
  return isValidReservationDate(next) ? next : null;
};

const exactDurationBetweenMinutes = (start: Date, end: Date): number | null => {
  const diff = end.getTime() - start.getTime();
  if (!Number.isFinite(diff)) return null;
  return diff / MINUTE_MS;
};

const hasOwn = (input: object, key: keyof ReservationTimingPolicyInput): boolean =>
  Object.prototype.hasOwnProperty.call(input, key);

export const buildReservationOccupancyInterval = (
  input: BuildReservationOccupancyIntervalInput
): ReservationOccupancyIntervalBuildResult => {
  const issues: ReservationOccupancyIntervalIssue[] = [];
  if (!isValidReservationDate(input.reservationStart)) {
    issues.push(issue('invalid_reservation_start', 'Reservation start must be a valid Date.', 'reservationStart'));
  }

  const explicitGuestEnd = input.guestEnd !== undefined;
  const guestEndInput = explicitGuestEnd && isValidReservationDate(input.guestEnd)
    ? cloneReservationDate(input.guestEnd)
    : null;
  if (explicitGuestEnd && !isValidReservationDate(input.guestEnd)) {
    issues.push(issue('invalid_guest_end', 'Guest end must be a valid Date when provided.', 'guestEnd'));
  }

  const durationMinutes = input.durationMinutes !== undefined ? finiteNumber(input.durationMinutes) : null;
  if (!explicitGuestEnd && (durationMinutes === null || durationMinutes <= 0)) {
    issues.push(issue('invalid_duration_minutes', 'Guest duration must be a finite positive minute value.', 'durationMinutes'));
  } else if (durationMinutes !== null && durationMinutes <= 0) {
    issues.push(issue('invalid_duration_minutes', 'Guest duration must be a finite positive minute value.', 'durationMinutes'));
  }

  const preparationMinutes = finiteIntegerMinutes(input.preparationMinutes ?? 0);
  if (preparationMinutes === null || preparationMinutes < 0) {
    issues.push(issue('invalid_preparation_minutes', 'Preparation duration must be a finite non-negative minute value.', 'preparationMinutes'));
  }

  const turnaroundMinutes = finiteIntegerMinutes(input.turnaroundMinutes);
  if (turnaroundMinutes === null || turnaroundMinutes < 0) {
    issues.push(issue('invalid_turnaround_minutes', 'Turnaround/buffer duration must be a finite non-negative minute value.', 'turnaroundMinutes'));
  }

  if (issues.length > 0 || preparationMinutes === null || turnaroundMinutes === null) {
    return { ok: false, interval: null, issues };
  }

  const reservationStart = cloneReservationDate(input.reservationStart);
  const durationGuestEnd = durationMinutes !== null ? addMinutes(reservationStart, durationMinutes) : null;
  if (durationMinutes !== null && !durationGuestEnd) {
    return {
      ok: false,
      interval: null,
      issues: [issue('invalid_date_arithmetic', 'Duration-based guest end date arithmetic produced an invalid Date.', 'durationMinutes')],
    };
  }

  if (guestEndInput && durationGuestEnd && guestEndInput.getTime() !== durationGuestEnd.getTime()) {
    return {
      ok: false,
      interval: null,
      issues: [issue('inconsistent_guest_end', 'guestEnd and durationMinutes must resolve to the same exact end boundary.', 'guestEnd')],
    };
  }

  const guestEnd = guestEndInput ?? durationGuestEnd;
  if (!guestEnd) {
    return {
      ok: false,
      interval: null,
      issues: [issue('invalid_date_arithmetic', 'Guest end date arithmetic produced an invalid Date.', 'guestEnd')],
    };
  }

  const effectiveDurationMinutes = exactDurationBetweenMinutes(reservationStart, guestEnd);
  if (effectiveDurationMinutes === null || effectiveDurationMinutes <= 0) {
    return {
      ok: false,
      interval: null,
      issues: [issue('invalid_guest_end', 'Guest end must be after reservation start.', 'guestEnd')],
    };
  }

  const blockedFrom = addMinutes(reservationStart, -preparationMinutes);
  if (!blockedFrom) {
    return {
      ok: false,
      interval: null,
      issues: [issue('invalid_date_arithmetic', 'Blocked-from date arithmetic produced an invalid Date.', 'blockedFrom')],
    };
  }

  const blockedUntil = addMinutes(guestEnd, turnaroundMinutes);
  if (!blockedUntil) {
    return {
      ok: false,
      interval: null,
      issues: [issue('invalid_date_arithmetic', 'Blocked-until date arithmetic produced an invalid Date.', 'blockedUntil')],
    };
  }

  if (!(reservationStart.getTime() < guestEnd.getTime()) || blockedUntil.getTime() < guestEnd.getTime() || blockedFrom.getTime() > reservationStart.getTime()) {
    return {
      ok: false,
      interval: null,
      issues: [issue('invalid_interval_order', 'Reservation interval ordering is invalid.')],
    };
  }

  return {
    ok: true,
    issues: [],
    interval: {
      reservationStart,
      guestEnd,
      blockedFrom,
      blockedUntil,
      durationMinutes: effectiveDurationMinutes,
      preparationMinutes,
      turnaroundMinutes,
    },
  };
};

export const doIntervalsOverlapHalfOpen = (
  leftStart: Date,
  leftEnd: Date,
  rightStart: Date,
  rightEnd: Date
): boolean => {
  if (![leftStart, leftEnd, rightStart, rightEnd].every(isValidReservationDate)) return false;
  return leftStart.getTime() < rightEnd.getTime() && rightStart.getTime() < leftEnd.getTime();
};

export const doReservationOccupancyIntervalsOverlap = (
  left: ReservationOccupancyInterval,
  right: ReservationOccupancyInterval
): boolean =>
  doIntervalsOverlapHalfOpen(left.blockedFrom, left.blockedUntil, right.blockedFrom, right.blockedUntil);

export const doReservationGuestIntervalsOverlap = (
  left: ReservationOccupancyInterval,
  right: ReservationOccupancyInterval
): boolean =>
  doIntervalsOverlapHalfOpen(left.reservationStart, left.guestEnd, right.reservationStart, right.guestEnd);

export const buildReservationOccupancyIntervalInputFromTiming = (
  input: ReservationTimingPolicyInput
):
  | { ok: true; value: BuildReservationOccupancyIntervalInput; issues: [] }
  | { ok: false; value: null; issues: ReservationOccupancyIntervalIssue[] } => {
  const issues: ReservationOccupancyIntervalIssue[] = [];
  const hasStartTime = hasOwn(input, 'startTime');
  const reservationStart = timestampLikeToDate(input.startTime);
  if (!hasStartTime || !reservationStart) {
    issues.push(issue('invalid_reservation_start', 'Legacy timing input did not contain a valid startTime.', 'startTime'));
  }

  const hasExplicitDuration = hasOwn(input, 'durationMinutes');
  const explicitDuration = finiteNumber(input.durationMinutes);
  if (hasExplicitDuration && (explicitDuration === null || explicitDuration <= 0)) {
    issues.push(issue('invalid_duration_minutes', 'Explicit durationMinutes must be a finite positive value.', 'durationMinutes'));
  }

  const hasDefaultDuration = hasOwn(input, 'defaultDurationMinutes');
  const defaultDuration = finiteNumber(input.defaultDurationMinutes);
  if (hasDefaultDuration && (defaultDuration === null || defaultDuration <= 0)) {
    issues.push(issue('invalid_duration_minutes', 'Default durationMinutes must be a finite positive value.', 'defaultDurationMinutes'));
  }

  const hasEndTime = hasOwn(input, 'endTime');
  const endTime = timestampLikeToDate(input.endTime);
  if (hasEndTime && input.endTime !== undefined && input.endTime !== null && !endTime) {
    issues.push(issue('invalid_guest_end', 'Provided endTime must be a valid date-like value.', 'endTime'));
  }
  let durationMinutes: number | null = null;
  let guestEnd: Date | null = null;

  if (explicitDuration !== null) {
    durationMinutes = explicitDuration;
  } else if (reservationStart && endTime) {
    const exactDuration = exactDurationBetweenMinutes(reservationStart, endTime);
    if (exactDuration === null || exactDuration <= 0) {
      issues.push(issue('invalid_guest_end', 'Provided endTime must be after startTime.', 'endTime'));
    } else {
      durationMinutes = exactDuration;
      guestEnd = endTime;
    }
  } else if (defaultDuration !== null) {
    durationMinutes = defaultDuration;
  } else {
    issues.push(issue('missing_duration_source', 'No reservation-specific, persisted end-time, or default duration source was available.'));
  }

  const hasExplicitPreparation = hasOwn(input, 'preparationMinutes');
  const explicitPreparation = finiteNumber(input.preparationMinutes);
  if (hasExplicitPreparation && (explicitPreparation === null || explicitPreparation < 0)) {
    issues.push(issue('invalid_preparation_minutes', 'Explicit preparationMinutes must be a finite non-negative value.', 'preparationMinutes'));
  }
  const hasDefaultPreparation = hasOwn(input, 'defaultPreparationMinutes');
  const defaultPreparation = finiteNumber(input.defaultPreparationMinutes);
  if (hasDefaultPreparation && (defaultPreparation === null || defaultPreparation < 0)) {
    issues.push(issue('invalid_preparation_minutes', 'Default preparationMinutes must be a finite non-negative value.', 'defaultPreparationMinutes'));
  }
  const preparationMinutes = explicitPreparation ?? defaultPreparation ?? 0;

  const hasExplicitTurnaround = hasOwn(input, 'turnaroundMinutes');
  const explicitTurnaround = finiteNumber(input.turnaroundMinutes);
  if (hasExplicitTurnaround && (explicitTurnaround === null || explicitTurnaround < 0)) {
    issues.push(issue('invalid_turnaround_minutes', 'Explicit turnaroundMinutes must be a finite non-negative value.', 'turnaroundMinutes'));
  }
  const hasExplicitBuffer = hasOwn(input, 'bufferMinutes');
  const explicitBuffer = finiteNumber(input.bufferMinutes);
  if (hasExplicitBuffer && (explicitBuffer === null || explicitBuffer < 0)) {
    issues.push(issue('invalid_turnaround_minutes', 'Explicit bufferMinutes must be a finite non-negative value.', 'bufferMinutes'));
  }
  const hasDefaultTurnaround = hasOwn(input, 'defaultTurnaroundMinutes');
  const defaultTurnaround = finiteNumber(input.defaultTurnaroundMinutes);
  if (hasDefaultTurnaround && (defaultTurnaround === null || defaultTurnaround < 0)) {
    issues.push(issue('invalid_turnaround_minutes', 'Default turnaroundMinutes must be a finite non-negative value.', 'defaultTurnaroundMinutes'));
  }
  const hasDefaultBuffer = hasOwn(input, 'defaultBufferMinutes');
  const defaultBuffer = finiteNumber(input.defaultBufferMinutes);
  if (hasDefaultBuffer && (defaultBuffer === null || defaultBuffer < 0)) {
    issues.push(issue('invalid_turnaround_minutes', 'Default bufferMinutes must be a finite non-negative value.', 'defaultBufferMinutes'));
  }
  const turnaroundMinutes = explicitTurnaround ?? explicitBuffer ?? defaultTurnaround ?? defaultBuffer ?? null;

  if (preparationMinutes === null || preparationMinutes < 0) {
    issues.push(issue('invalid_preparation_minutes', 'No finite non-negative preparation source was available.', 'preparationMinutes'));
  }

  if (turnaroundMinutes === null) {
    issues.push(issue('invalid_turnaround_minutes', 'No finite turnaround/buffer source was available.', 'turnaroundMinutes'));
  }

  if (issues.length > 0 || !reservationStart || durationMinutes === null || preparationMinutes === null || turnaroundMinutes === null) {
    return { ok: false, value: null, issues };
  }

  const built = buildReservationOccupancyInterval({ reservationStart, durationMinutes, guestEnd: guestEnd ?? undefined, preparationMinutes, turnaroundMinutes });
  if (!built.ok) {
    return { ok: false, value: null, issues: built.issues };
  }

  return {
    ok: true,
    value: { reservationStart, guestEnd: guestEnd ?? undefined, durationMinutes: built.interval.durationMinutes, preparationMinutes: built.interval.preparationMinutes, turnaroundMinutes: built.interval.turnaroundMinutes },
    issues: [],
  };
};

export const buildReservationOccupancyIntervalFromTiming = (
  input: ReservationTimingPolicyInput
): ReservationOccupancyIntervalBuildResult => {
  const canonicalInput = buildReservationOccupancyIntervalInputFromTiming(input);
  if (!canonicalInput.ok) return { ok: false, interval: null, issues: canonicalInput.issues };
  return buildReservationOccupancyInterval(canonicalInput.value);
};
